import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BroadcastService, HttpService, MessageService, UserService,PermissionService } from 'src/app/core';
import {
  NgbActiveModal,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import { PathConfig } from 'src/app/core/config/path.config';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MsgConstantConfig } from 'src/app/core/config/message.constant.config';
import { Utility } from 'src/app/core/utils/utility';
import { CustomDateParserFormatter } from 'src/app/shared/services/custom-date-parser.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ConstantConfig } from 'src/app/core/config/constant.config';

@Component({
  selector: 'app-start-stop-interest-accrual',
  templateUrl: './start-stop-interest-accrual.component.html',
  styleUrls: ['./start-stop-interest-accrual.component.scss'],
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class StartStopInterestAccrualComponent implements OnInit {
  @Input() loanDetail!: any;
  @Input() loanID!: any;
  @Input() isRouteShowHistory: boolean = false;
  @Input() operationType: string = '';
  @Input() isEditMode = false;
  @Input() editTransaction: any;

  modalTitle: string = '';
  modalSubTitle: string = '';
  fullname: string = '';
  validDates: string[] = [];
  private validDatesSet = new Set<string>();
  /** API error code when valid_dates cannot be loaded (e.g. LOAN_PAID_OFF). */
  private validDatesErrorCode: string | null = null;
  historyList: any[] = [];
  selectedDate: string = '';
  originalEffectiveDate: string = '';
  displaySelectedDate: string = '';
  insertPreviewIndex: number = -1;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  private effectiveDateTouched = false;
  accrualMinDate: NgbDateStruct = { year: 1920, month: 1, day: 1 };
  accrualMaxDate: NgbDateStruct = { year: 9999, month: 12, day: 31 };
  calendarStartDate: NgbDateStruct = { year: 1920, month: 1, day: 1 };

  constructor(
    private httpService: HttpService,
    private msgService: MessageService,
    public activeModal: NgbActiveModal,
    private broadCastService: BroadcastService,
    private translateService: TranslateService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    if (this.operationType === 'START_INT') {
      this.modalTitle = this.isEditMode
        ? 'Edit Start Interest Accrual'
        : 'Start Interest Accrual';
      this.modalSubTitle = this.isEditMode
        ? 'Update the effective date.'
        : 'Select the date from which interest accrual should start.';
    } else {
      this.modalTitle = this.isEditMode
        ? 'Edit Stop Interest Accrual'
        : 'Stop Interest Accrual';
      this.modalSubTitle = this.isEditMode
        ? 'Update the effective date.'
        : 'Select the date from which interest accrual should stop.';
    }

    this.fullname = `${this.userService.getUserInfo('firstname')} ${this.userService.getUserInfo('middlename')} ${this.userService.getUserInfo('lastname')}`;
    this.getValidDatesAndHistory();
  }

  instantTranslateService(key: string = ''): string {
    return this.translateService.instant(key);
  }

  getValidDatesAndHistory(): void {
    let url =
      `${PathConfig.LOAN_TRANSACTIONS_START_STOP_ACCURAL_VALID_DATES}` +
      `?loan_id=${this.loanID}` +
      `&payment_info_code_slug=${this.operationType}`;

    // Edit mode must tell the API to bypass the transaction being edited.
    if (this.isEditMode && this.editTransaction?.transaction_id) {
      url +=
        `&operation_mode=edit` +
        `&transaction_id=${this.editTransaction.transaction_id}`;
    }

    this.isLoading = true;
    this.validDatesErrorCode = null;
    this.httpService.get(url).subscribe({
      next: (res: any) => {
        this.validDatesErrorCode = res?.error || null;
        this.validDates = (res?.valid_dates || [])
          .slice()
          .sort(
            (a: string, b: string) =>
              this.getEffectiveDateValue(a) - this.getEffectiveDateValue(b)
          );
        this.validDatesSet = new Set(this.validDates);

        if (this.isEditMode) {
          this.ensureCurrentEditDateIsSelectable();
        }

        this.syncCalendarBoundsFromValidDates();
        this.calendarStartDate = this.resolveCalendarStartDate();

        const sortedHistory = (res?.marker_transactions || [])
          .slice()
          .sort(
            (a: any, b: any) =>
              this.getEffectiveDateValue(b.payment_effective_date) -
              this.getEffectiveDateValue(a.payment_effective_date)
          );

        // Keep full history in edit mode so the original edited row stays visible.
        this.historyList = sortedHistory;

        if (this.isEditMode) {
          this.originalEffectiveDate = this.getEffectiveDateOnly(
            this.editTransaction?.payment_effective_date
          );
          if (this.originalEffectiveDate) {
            this.onDateChange(this.originalEffectiveDate);
          }
        } else {
          const defaultDate = this.resolveDefaultDate();
          if (defaultDate) {
            this.onDateChange(defaultDate);
          }
        }

        this.calendarStartDate = this.resolveCalendarStartDate();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const msgList = Utility.getErrorMessage(err.error);
        msgList.forEach((msg) => this.msgService.showError(msg));
      }
    });
  }

  /**
   * Disable dates that are not in valid_dates.
   * Intentionally ignores current view month so outside-month days (e.g. July
   * while August is open) remain selectable when they are valid.
   */
  markDisabled = (date: NgbDateStruct): boolean => {
    if (!this.validDatesSet.size) {
      return true;
    }
    return !this.validDatesSet.has(this.toDateKey(date));
  };

  isValidCalendarDate(date: NgbDateStruct): boolean {
    return this.validDatesSet.has(this.toDateKey(date));
  }

  openCalendar(dp: NgbInputDatepicker): void {
    this.calendarStartDate = this.resolveCalendarStartDate();
    dp.toggle();
    if (dp.isOpen()) {
      dp.navigateTo(this.calendarStartDate);
    }
  }
  formatTransactionDate(date: any): string {
    if (!date) {
      return '';
    }
  
    return moment
      .tz(date, ConstantConfig.SERVER_TIMEZONE)
      .local()
      .format('MM/DD/YYYY h:mm:ss A');
  }
  formatDisplayDate(dateValue: string): string {
    const normalized = this.normalizeDate(dateValue) || this.getEffectiveDateOnly(dateValue);
    if (!normalized) {
      return '';
    }
    return moment(normalized, 'YYYY-MM-DD').format('MM/DD/YYYY');
  }

  onDateChange(value: string): void {
    if (!value) {
      this.selectedDate = '';
      this.displaySelectedDate = '';
      this.insertPreviewIndex = -1;
      return;
    }

    const normalized = this.normalizeDate(value);
    if (normalized && this.validDatesSet.has(normalized)) {
      this.selectedDate = normalized;
      this.displaySelectedDate = this.formatDisplayDate(normalized);
      this.calendarStartDate = this.toNgbDate(normalized);
      this.updateInsertPreviewIndex();
      return;
    }

    // Allow incomplete typed values without clearing the input while typing.
    this.selectedDate = value;
    this.displaySelectedDate = '';
    this.insertPreviewIndex = -1;
  }

  onDateBlur(status?: string | null): void {
    this.effectiveDateTouched = true;
    const normalized = this.normalizeDate(this.selectedDate);
    if (status === 'INVALID' || !normalized || !this.validDatesSet.has(normalized)) {
      this.selectedDate = '';
      this.displaySelectedDate = '';
      this.insertPreviewIndex = -1;
      return;
    }

    this.selectedDate = normalized;
    this.displaySelectedDate = this.formatDisplayDate(normalized);
    this.calendarStartDate = this.toNgbDate(normalized);
    this.updateInsertPreviewIndex();
  }

  get showEffectiveDateRequired(): boolean {
    if (!this.hasValidDates || !this.effectiveDateTouched) {
      return false;
    }
    const raw = this.selectedDate;
    return raw == null || String(raw).trim() === '';
  }

  get canSubmit(): boolean {
    const normalized = this.normalizeDate(this.selectedDate);
    const isValidSelectedDate =
      !!normalized && this.validDatesSet.has(normalized);

    if (!isValidSelectedDate || this.isLoading || this.isSubmitting) {
      return false;
    }

    if (this.isEditMode) {
      return normalized !== this.originalEffectiveDate;
    }

    return !!this.validDates?.length;
  }

  get hasValidDates(): boolean {
    return !this.isLoading && this.validDates.length > 0;
  }

  get noValidDatesMessage(): string {
    if (this.validDatesErrorCode === 'LOAN_PAID_OFF') {
      return this.translateService.instant(
        `${this.operationType === 'START_INT' ? 'START' : 'STOP' } Interest transactions are not allowed on paid-off loans.`
      );
    }

    return this.translateService.instant(
      'This action cannot be performed because the loan is already in the {{state}} state.',
      { state: this.operationType === 'START_INT' ? 'START INT' : 'STOP INT' }
    );
  }

  get accrualStatusBefore(): string {
    const latest = this.getLatestHistoryTransaction();
    if (latest?.payment_info_code_slug) {
      return latest.payment_info_code_slug;
    }

    // Fallback when history is empty.
    if (this.loanDetail?.interest_accrual_on_yn === 'YES') {
      return 'START_INT';
    }
    if (this.loanDetail?.interest_accrual_on_yn === 'NO') {
      return 'STOP_INT';
    }
    return this.operationType === 'START_INT' ? 'STOP_INT' : 'START_INT';
  }

  get hasSelectedEffectiveDate(): boolean {
    const normalized = this.normalizeDate(this.selectedDate);
    return !!normalized && this.validDatesSet.has(normalized);
  }

  get accrualStatusAfter(): string {
    const selectedDate = this.normalizeDate(this.selectedDate);

    // No date selected yet — After should display as placeholder "-".
    if (!selectedDate) {
      return '';
    }

    const latest = this.getLatestHistoryTransaction();

    // No history — the new operation becomes the loan state.
    if (!latest) {
      return this.operationType;
    }

    const latestDate =
      this.normalizeDate(this.getEffectiveDateOnly(latest.payment_effective_date)) ||
      this.getEffectiveDateOnly(latest.payment_effective_date);


    if (selectedDate >= latestDate) {
      return this.operationType;
    }


    return this.accrualStatusBefore;
  }

  get accrualStatusAfterDisplay(): string {
    return this.hasSelectedEffectiveDate ? this.accrualStatusAfter : '-';
  }

  get afterDateTooltip(): string {
    return this.instantTranslateService('Please select effective date first.');
  }


  private isInactiveAccrualTransaction(item: any): boolean {
    if (!item) {
      return true;
    }

    if (item.is_reversed === true || item.is_reversed === 1 || item.is_reversed === '1') {
      return true;
    }

    if (String(item.payment_status || '').toUpperCase() === 'REVERSED') {
      return true;
    }

    return item.reverse_transaction_id != null && item.reverse_transaction_id !== '';
  }

  private getLatestHistoryTransaction(): any | null {
    if (!this.historyList?.length) {
      return null;
    }


    return (
      this.historyList.find((item) => !this.isInactiveAccrualTransaction(item)) ?? null
    );
  }

  get previewMessage(): string {
    if (!this.isEditMode) {
      return this.instantTranslateService(
        'transaction will be created here, as per your selected effective date'
      );
    }

    const selectedValue = this.getEffectiveDateValue(this.selectedDate);
    const originalValue = this.getEffectiveDateValue(this.originalEffectiveDate);

    if (selectedValue > originalValue) {
      return this.instantTranslateService(
        'transaction will be updated here, as per your selected effective date'
      );
    }

    if (selectedValue < originalValue) {
      return this.instantTranslateService(
        'transaction will be updated and moved earlier here, as per your selected effective date'
      );
    }

    return this.instantTranslateService(
      'transaction will be updated here, as per your selected effective date'
    );
  }

  isEditingTransaction(item: any): boolean {
    return this.isEditMode && item?.id === this.editTransaction?.transaction_id;
  }

  updateInsertPreviewIndex(): void {
    if (!this.selectedDate) {
      this.insertPreviewIndex = -1;
      return;
    }

    // In edit mode, show the update preview only after the date actually changes.
    if (this.isEditMode && this.selectedDate === this.originalEffectiveDate) {
      this.insertPreviewIndex = -1;
      return;
    }

    const selectedValue = this.getEffectiveDateValue(this.selectedDate);

    if (!this.isEditMode) {
      const index = this.historyList.findIndex(
        (item) => this.getEffectiveDateValue(item.payment_effective_date) <= selectedValue
      );
      this.insertPreviewIndex = index === -1 ? this.historyList.length : index;
      return;
    }

    const editIndex = this.historyList.findIndex(
      (item) => item.id === this.editTransaction?.transaction_id
    );
    const originalValue = this.getEffectiveDateValue(this.originalEffectiveDate);
    const movedForward = selectedValue > originalValue;

    const placementList = this.historyList.filter(
      (item) => item.id !== this.editTransaction?.transaction_id
    );
    const filteredIndex = placementList.findIndex(
      (item) => this.getEffectiveDateValue(item.payment_effective_date) <= selectedValue
    );
    const resolvedFilteredIndex =
      filteredIndex === -1 ? placementList.length : filteredIndex;

    let chronologicalIndex: number;
    if (resolvedFilteredIndex >= placementList.length) {
      chronologicalIndex = this.historyList.length;
    } else {
      const targetItem = placementList[resolvedFilteredIndex];
      chronologicalIndex = this.historyList.findIndex(
        (item) => item.id === targetItem.id
      );
    }

    if (editIndex < 0) {
      this.insertPreviewIndex = chronologicalIndex;
      return;
    }

    if (movedForward) {
      this.insertPreviewIndex =
        chronologicalIndex > editIndex ? editIndex : chronologicalIndex;
    } else {
      this.insertPreviewIndex =
        chronologicalIndex <= editIndex ? editIndex + 1 : chronologicalIndex;
    }
  }

  shouldShowPreviewBefore(index: number): boolean {
    return !!this.displaySelectedDate && this.insertPreviewIndex === index;
  }

  get showPreviewAtEnd(): boolean {
    return !!this.displaySelectedDate && this.insertPreviewIndex === this.historyList.length;
  }

  getEffectiveDateOnly(dateValue: string): string {
    return (dateValue || '').toString().substring(0, 10);
  }

  submit(): void {
    const paymentEffectiveDate = this.normalizeDate(this.selectedDate);
    if (!paymentEffectiveDate || !this.validDatesSet.has(paymentEffectiveDate)) {
      this.msgService.showError('Please select a valid effective date.');
      return;
    }

    if (this.isEditMode) {
      this.submitEdit(paymentEffectiveDate);
      return;
    }

    const params = {
      loan_id: this.loanID,
      payment_info_code_slug: this.operationType,
      payment_effective_date: paymentEffectiveDate
    };
    const url = `${PathConfig.LOAN_TRANSACTIONS_START_STOP_ACCURAL}`;
    this.isSubmitting = true;

    this.httpService.post(url, params).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.msgService.showSuccess(res?.message);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_QUEUE_DETAIL_DATA);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_LOAN_TRANSACTION_LIST);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_ACCOUNT_HISTORY_LIST);
        this.activeModal.close(true);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msgList = Utility.getErrorMessage(err.error);
        msgList.forEach((msg) => this.msgService.showError(msg));
      }
    });
  }

  private submitEdit(paymentEffectiveDate: string): void {
    const params = {
      transaction_id: this.editTransaction.transaction_id,
      payment_effective_date: paymentEffectiveDate
    };
    const url = PathConfig.LOAN_TRANSACTIONS_EDIT_SYSTEM_TRANSACTIONS;
    this.isSubmitting = true;

    this.httpService.put(url, params).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.msgService.showSuccess(res?.message);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_QUEUE_DETAIL_DATA);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_LOAN_TRANSACTION_LIST);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_ACCOUNT_HISTORY_LIST);
        this.activeModal.close(true);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msgList = Utility.getErrorMessage(err.error);
        msgList.forEach((msg) => this.msgService.showError(msg));
      }
    });
  }

  private ensureCurrentEditDateIsSelectable(): void {
    const currentDate = this.getEffectiveDateOnly(
      this.editTransaction?.payment_effective_date
    );
    if (!currentDate || this.validDatesSet.has(currentDate)) {
      return;
    }

    this.validDates = [...this.validDates, currentDate].sort();
    this.validDatesSet.add(currentDate);
  }

  private syncCalendarBoundsFromValidDates(): void {
    if (!this.validDates.length) {
      return;
    }
    this.accrualMinDate = this.toNgbDate(this.getEffectiveDateOnly(this.validDates[0]));
    this.accrualMaxDate = this.toNgbDate(
      this.getEffectiveDateOnly(this.validDates[this.validDates.length - 1])
    );
  }

  private resolveCalendarStartDate(): NgbDateStruct {
    const selected = this.normalizeDate(this.selectedDate);
    if (selected && this.validDatesSet.has(selected)) {
      return this.toNgbDate(selected);
    }

    if (this.validDates.length) {
      return this.toNgbDate(
        this.getEffectiveDateOnly(this.validDates[this.validDates.length - 1])
      );
    }

    return this.accrualMinDate;
  }

  private resolveDefaultDate(): string {
    if (!this.validDates.length) {
      return '';
    }

    const lastValidDate = this.getEffectiveDateOnly(
      this.validDates[this.validDates.length - 1]
    );
    const systemToday = this.getEffectiveDateOnly(
      this.loanDetail?.last_nightly_process_date
    );

    // Auto-select only when last valid date matches system today (loan last nightly process date).
    if (lastValidDate && systemToday && lastValidDate === systemToday) {
      return lastValidDate;
    }

    // Otherwise leave unselected; user must choose a required date.
    return '';
  }

  private getEffectiveDateValue(dateValue: string): number {
    const dateOnly = this.getEffectiveDateOnly(dateValue);
    return moment(dateOnly, 'YYYY-MM-DD').valueOf() || 0;
  }

  private normalizeDate(value: string): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = moment(trimmed, ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  private toDateKey(date: NgbDateStruct): string {
    return `${date.year}-${('0' + date.month).slice(-2)}-${('0' + date.day).slice(-2)}`;
  }

  private toNgbDate(dateStr: string): NgbDateStruct {
    const [year, month, day] = dateStr.split('-').map(Number);
    return { year, month, day };
  }
}
